import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import client from "../api";
import Logo from "./Logo";
import { ChevronDown, LogOut, LayoutDashboard, ShieldCheck, Globe, ShoppingBag } from "lucide-react";

const LANGS = [
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
  { code: "es", label: "ES" },
];

export default function Nav({ variant = "marketing" }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [newOrders, setNewOrders] = React.useState(0);
  const [hasCustomer, setHasCustomer] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    setHasCustomer(!!localStorage.getItem("foodgo_customer_token"));
  }, []);

  React.useEffect(() => {
    if (!user || user.role !== "restaurateur") return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await client.get("/orders/new-count");
        if (alive) setNewOrders(Number(data.count || 0));
      } catch (e) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 12000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  const logoutCustomer = () => {
    try { localStorage.removeItem("foodgo_customer_token"); localStorage.removeItem("foodgo_customer"); } catch { /* ignore */ }
    setHasCustomer(false);
    navigate("/");
  };

  // Customer must NEVER see the top navigation menu (privacy + UX). Hooks above MUST run first.
  if (hasCustomer && !user) return null;

  return (
    <header className="sticky top-0 z-50 glass-nav" data-testid="main-nav">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-2">
        <Logo size="md" />

        {variant === "marketing" && (
          <nav className="hidden md:flex items-center gap-7 text-xs font-medium text-zinc-300 font-mono uppercase tracking-widest">
            <a href="#features" className="hover:text-neon-cyan transition" data-testid="nav-features">{t.nav.features}</a>
            <a href="#pricing" className="hover:text-neon-cyan transition" data-testid="nav-pricing">{t.nav.pricing}</a>
            <a href="#how" className="hover:text-neon-cyan transition" data-testid="nav-how">{t.nav.howItWorks}</a>
          </nav>
        )}

        <div className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setOpen(!open)} className="btn-outline !py-1.5 !px-3 text-[11px]" data-testid="lang-toggle">
              <Globe className="w-3 h-3" /> {lang.toUpperCase()} <ChevronDown className="w-3 h-3" />
            </button>
            {open && (
              <div className="absolute right-0 mt-2 bg-deep-card border border-line rounded-xl overflow-hidden shadow-lg">
                {LANGS.map((l) => (
                  <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${lang === l.code ? "text-neon-cyan font-semibold" : "text-zinc-300"}`}
                    data-testid={`lang-${l.code}`}>{l.label}</button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <>
              {user.role === "super_admin" ? (
                <Link to="/super-admin" className="btn-outline !py-1.5 text-xs" data-testid="nav-admin">
                  <ShieldCheck className="w-3.5 h-3.5" /> {t.nav.admin}
                </Link>
              ) : (
                <Link to="/dashboard" className="btn-outline !py-1.5 text-xs relative" data-testid="nav-dashboard">
                  <LayoutDashboard className="w-3.5 h-3.5" /> {t.nav.dashboard}
                  {newOrders > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-neon-yellow text-[#06061a] text-[9px] font-bold font-mono flex items-center justify-center glow-yellow" data-testid="nav-orders-badge">
                      {newOrders > 9 ? "9+" : newOrders}
                    </span>
                  )}
                </Link>
              )}
              <button onClick={async () => { await logout(); navigate("/"); }} className="btn-outline !py-1.5 !px-2.5 text-xs" data-testid="nav-logout">
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </>
          ) : hasCustomer ? (
            <>
              <Link to="/my-orders" className="btn-outline !py-1.5 text-xs" data-testid="nav-my-orders">
                <ShoppingBag className="w-3.5 h-3.5" /> {t.nav.myOrders || "I miei ordini"}
              </Link>
              <button onClick={logoutCustomer} className="btn-outline !py-1.5 !px-2.5 text-xs" data-testid="nav-customer-logout">
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost text-xs" data-testid="nav-login">{t.nav.login}</Link>
              <Link to="/register" className="btn-primary !py-1.5 text-xs" data-testid="nav-register">{t.nav.register}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
